// src/pages/Campaigns/SurveyGenie/SurveyFAQs.js
import React, { useState, useEffect } from 'react';
import {
  Box, Typography, Button, Paper, Grid, Card, CardContent, CardActions,
  IconButton, TextField, InputAdornment, Dialog, DialogTitle, DialogContent,
  DialogActions, Alert, CircularProgress, Chip, Tooltip, Accordion,
  AccordionSummary, AccordionDetails, Checkbox, Snackbar, Divider, Stack
} from '@mui/material';
import {
  Add as AddIcon,
  Delete as DeleteIcon,
  Edit as EditIcon,
  Search as SearchIcon,
  QuestionAnswer as FAQIcon,
  CheckCircle as CheckCircleIcon,
  ArrowBack as BackIcon,
  ExpandMore as ExpandMoreIcon,
  ContentCopy as CopyIcon,
} from '@mui/icons-material';
import ModelTrainingIcon from '@mui/icons-material/ModelTraining';
import { useParams, useNavigate } from 'react-router-dom';
import axiosInstance from '../../../utils/axiosInstance';
import { hasPermission } from '../../../utils/permissionUtils';

const SurveyFAQs = () => {
  const { surveyId, chatbotId } = useParams();
  const navigate = useNavigate();

  // State
  const [faqs, setFaqs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedFaqs, setSelectedFaqs] = useState([]);
  const [isTraining, setIsTraining] = useState(false);

  // Dialog states
  const [createDialog, setCreateDialog] = useState(false);
  const [editDialog, setEditDialog] = useState({ open: false, faq: null });
  const [deleteDialog, setDeleteDialog] = useState({ open: false, faq: null });
  const [bulkCreateDialog, setBulkCreateDialog] = useState(false);

  // Form state
  const [faqForm, setFaqForm] = useState({ question: '', answer: '', category: '', tags: '' });
  const [bulkFaqs, setBulkFaqs] = useState('');
  const [formError, setFormError] = useState('');

  // Permissions
  const [canModify, setCanModify] = useState(false);
  const [canDelete, setCanDelete] = useState(false);

  // Snackbar
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });

  useEffect(() => {
    const checkPermissions = async () => {
      const modifyPerm = await hasPermission('survey_genie', 'add|edit');
      const deletePerm = await hasPermission('survey_genie', 'delete');
      setCanModify(modifyPerm);
      setCanDelete(deletePerm);
    };
    checkPermissions();
  }, []);

  useEffect(() => {
    if (surveyId) {
      loadFAQs();
    }
  }, [surveyId]);

  const loadFAQs = async () => {
    setLoading(true);
    try {
      const response = await axiosInstance.get('/api/surveys/faqs', {
        params: { survey_id: surveyId }
      });
      if (response.data.success) {
        setFaqs(response.data.data);
      }
    } catch (error) {
      showSnackbar('Error loading FAQs', 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateFAQ = async () => {
    setFormError('');
    if (!faqForm.question.trim()) {
      setFormError('Question is required');
      return;
    }
    if (!faqForm.answer.trim()) {
      setFormError('Answer is required');
      return;
    }

    try {
      const response = await axiosInstance.post('/api/surveys/faqs', faqForm, {
        params: { survey_id: surveyId }
      });

      if (response.data.success) {
        showSnackbar(response.data.message || 'FAQ created and trained to Knowledge base successfully', 'success');
        setCreateDialog(false);
        setFaqForm({ question: '', answer: '', category: '', tags: '' });
        loadFAQs();
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Error creating FAQ', 'error');
    }
  };

  const handleUpdateFAQ = async () => {
    if (!editDialog.faq) return;
    setFormError('');

    if (!faqForm.question.trim() || !faqForm.answer.trim()) {
      setFormError('Question and Answer are required');
      return;
    }

    try {
      const response = await axiosInstance.put('/api/surveys/faqs', faqForm, {
        params: { faq_id: editDialog.faq.id }
      });

      if (response.data.success) {
        showSnackbar('FAQ updated successfully', 'success');
        setEditDialog({ open: false, faq: null });
        loadFAQs();
      }
    } catch (error) {
      showSnackbar('Error updating FAQ', 'error');
    }
  };

  const handleDeleteFAQ = async () => {
    if (!deleteDialog.faq) return;

    try {
      await axiosInstance.delete('/api/surveys/faqs', {
        params: { faq_id: deleteDialog.faq.id }
      });
      showSnackbar('FAQ deleted successfully', 'success');
      setDeleteDialog({ open: false, faq: null });
      loadFAQs();
    } catch (error) {
      showSnackbar('Error deleting FAQ', 'error');
    }
  };

  const handleBulkCreate = async () => {
    if (!bulkFaqs.trim()) {
      showSnackbar('Please enter FAQs', 'error');
      return;
    }

    // Parse bulk FAQs (format: Q: question\nA: answer\n\n)
    const faqPairs = bulkFaqs.split(/\n\n+/).filter(pair => pair.trim());
    const parsedFaqs = [];

    for (const pair of faqPairs) {
      const lines = pair.split('\n');
      let question = '';
      let answer = '';

      for (const line of lines) {
        if (line.toLowerCase().startsWith('q:')) {
          question = line.substring(2).trim();
        } else if (line.toLowerCase().startsWith('a:')) {
          answer = line.substring(2).trim();
        }
      }

      if (question && answer) {
        parsedFaqs.push({ question, answer });
      }
    }

    if (parsedFaqs.length === 0) {
      showSnackbar('No valid FAQs found. Use format: Q: question\\nA: answer', 'error');
      return;
    }

    try {
      const response = await axiosInstance.post('/api/surveys/faqs/bulk',
        { faqs: parsedFaqs },
        { params: { survey_id: surveyId } }
      );

      if (response.data.success) {
        showSnackbar(response.data.message || `Created and trained ${response.data.data.length} FAQ(s) to Knowledge base`, 'success');
        setBulkCreateDialog(false);
        setBulkFaqs('');
        loadFAQs();
      }
    } catch (error) {
      showSnackbar('Error creating FAQs', 'error');
    }
  };

  const handleTrainSelected = async () => {
    const untrainedIds = selectedFaqs.filter(id => {
      const faq = faqs.find(f => f.id === id);
      return faq && !faq.is_trained;
    });

    if (untrainedIds.length === 0) {
      showSnackbar('No untrained FAQs selected', 'warning');
      return;
    }

    setIsTraining(true);
    try {
      const response = await axiosInstance.post('/api/surveys/faqs/train',
        { faq_ids: untrainedIds },
        { params: { survey_id: surveyId } }
      );

      if (response.data.success) {
        showSnackbar(response.data.message, 'success');
        setSelectedFaqs([]);
        loadFAQs();
      }
    } catch (error) {
      showSnackbar(error.response?.data?.message || 'Training failed', 'error');
    } finally {
      setIsTraining(false);
    }
  };

  const handleSelectFaq = (faqId) => {
    setSelectedFaqs(prev =>
      prev.includes(faqId) ? prev.filter(id => id !== faqId) : [...prev, faqId]
    );
  };

  const handleSelectAll = () => {
    if (selectedFaqs.length === filteredFaqs.length) {
      setSelectedFaqs([]);
    } else {
      setSelectedFaqs(filteredFaqs.map(f => f.id));
    }
  };

  const openEditDialog = (faq) => {
    setFaqForm({
      question: faq.question,
      answer: faq.answer,
      category: faq.category || '',
      tags: faq.tags || ''
    });
    setEditDialog({ open: true, faq });
  };

  const showSnackbar = (message, severity = 'info') => {
    setSnackbar({ open: true, message, severity });
  };

  const filteredFaqs = faqs.filter(faq =>
    faq.question.toLowerCase().includes(searchTerm.toLowerCase()) ||
    faq.answer.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (faq.category && faq.category.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 5, mt: 10 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3, mt: 10 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center' }}>
          <IconButton onClick={() => navigate(`/chatbot/${chatbotId}/surveys`)} sx={{ mr: 2 }}>
            <BackIcon />
          </IconButton>
          <Box>
            <Typography variant="h5" fontWeight={700}>
              <FAQIcon sx={{ mr: 1, verticalAlign: 'middle' }} />
              Survey FAQs
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Create FAQ-style training data for your survey chatbot
            </Typography>
          </Box>
        </Box>
        {canModify && (
          <Stack direction="row" spacing={2}>
            <Button variant="outlined" startIcon={<CopyIcon />} onClick={() => setBulkCreateDialog(true)}>
              Bulk Add
            </Button>
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialog(true)}>
              Add FAQ
            </Button>
          </Stack>
        )}
      </Box>

      {/* Training Action Bar */}
      {selectedFaqs.length > 0 && canModify && (
        <Paper sx={{ p: 2, mb: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
          <Chip label={`${selectedFaqs.length} FAQ(s) selected`} color="primary" variant="outlined" />
          <Button
            variant="contained"
            color="primary"
            startIcon={isTraining ? <CircularProgress size={20} color="inherit" /> : <ModelTrainingIcon />}
            onClick={handleTrainSelected}
            disabled={isTraining}
          >
            {isTraining ? 'Training...' : 'Train Selected'}
          </Button>
          <Button variant="text" size="small" onClick={() => setSelectedFaqs([])}>
            Clear Selection
          </Button>
        </Paper>
      )}

      {/* Search & Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid item xs={12} md={6}>
            <TextField
              fullWidth
              size="small"
              placeholder="Search FAQs..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                )
              }}
            />
          </Grid>
          <Grid item xs={12} md={3}>
            <Button variant="outlined" size="small" onClick={handleSelectAll} fullWidth>
              {selectedFaqs.length === filteredFaqs.length && filteredFaqs.length > 0
                ? 'Deselect All' : 'Select All'}
            </Button>
          </Grid>
          <Grid item xs={12} md={3}>
            <Typography variant="body2" color="text.secondary">
              {filteredFaqs.length} FAQ(s)
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {/* FAQs List */}
      {filteredFaqs.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: 'center' }}>
          <FAQIcon sx={{ fontSize: 64, color: 'grey.400', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No FAQs yet
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
            Create question-answer pairs to train your survey chatbot
          </Typography>
          {canModify && (
            <Button variant="contained" startIcon={<AddIcon />} onClick={() => setCreateDialog(true)}>
              Add First FAQ
            </Button>
          )}
        </Paper>
      ) : (
        <Box>
          {filteredFaqs.map((faq) => (
            <Accordion key={faq.id} sx={{ mb: 1 }}>
              <AccordionSummary
                expandIcon={<ExpandMoreIcon />}
                sx={{ '& .MuiAccordionSummary-content': { alignItems: 'center' } }}
              >
                <Checkbox
                  checked={selectedFaqs.includes(faq.id)}
                  onChange={(e) => {
                    e.stopPropagation();
                    handleSelectFaq(faq.id);
                  }}
                  onClick={(e) => e.stopPropagation()}
                  sx={{ mr: 1 }}
                />
                <Box sx={{ flexGrow: 1 }}>
                  <Typography fontWeight={600}>{faq.question}</Typography>
                  <Box sx={{ display: 'flex', gap: 1, mt: 0.5 }}>
                    {faq.category && <Chip label={faq.category} size="small" variant="outlined" />}
                    {faq.is_trained ? (
                      <Chip icon={<CheckCircleIcon />} label="Trained" size="small" color="success" variant="outlined" />
                    ) : (
                      <Chip label="Not Trained" size="small" variant="outlined" />
                    )}
                  </Box>
                </Box>
              </AccordionSummary>
              <AccordionDetails>
                <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', mb: 2 }}>
                  {faq.answer}
                </Typography>
                {faq.tags && (
                  <Typography variant="caption" color="text.secondary">
                    Tags: {faq.tags}
                  </Typography>
                )}
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                  {canModify && (
                    <Button size="small" startIcon={<EditIcon />} onClick={() => openEditDialog(faq)}>
                      Edit
                    </Button>
                  )}
                  {canDelete && (
                    <Button size="small" color="error" startIcon={<DeleteIcon />} onClick={() => setDeleteDialog({ open: true, faq })}>
                      Delete
                    </Button>
                  )}
                </Box>
              </AccordionDetails>
            </Accordion>
          ))}
        </Box>
      )}

      {/* Create Dialog */}
      <Dialog open={createDialog} onClose={() => setCreateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Add New FAQ</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <TextField
            fullWidth
            label="Question"
            value={faqForm.question}
            onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
            required
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="Answer"
            value={faqForm.answer}
            onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
            sx={{ mb: 2 }}
            required
            multiline
            rows={4}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Category (optional)"
                value={faqForm.category}
                onChange={(e) => setFaqForm({ ...faqForm, category: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Tags (comma separated)"
                value={faqForm.tags}
                onChange={(e) => setFaqForm({ ...faqForm, tags: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleCreateFAQ}>Create</Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialog.open} onClose={() => setEditDialog({ open: false, faq: null })} maxWidth="md" fullWidth>
        <DialogTitle>Edit FAQ</DialogTitle>
        <DialogContent>
          {formError && <Alert severity="error" sx={{ mb: 2 }}>{formError}</Alert>}
          <TextField
            fullWidth
            label="Question"
            value={faqForm.question}
            onChange={(e) => setFaqForm({ ...faqForm, question: e.target.value })}
            sx={{ mt: 2, mb: 2 }}
            required
            multiline
            rows={2}
          />
          <TextField
            fullWidth
            label="Answer"
            value={faqForm.answer}
            onChange={(e) => setFaqForm({ ...faqForm, answer: e.target.value })}
            sx={{ mb: 2 }}
            required
            multiline
            rows={4}
          />
          <Grid container spacing={2}>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Category (optional)"
                value={faqForm.category}
                onChange={(e) => setFaqForm({ ...faqForm, category: e.target.value })}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="Tags (comma separated)"
                value={faqForm.tags}
                onChange={(e) => setFaqForm({ ...faqForm, tags: e.target.value })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog({ open: false, faq: null })}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateFAQ}>Save</Button>
        </DialogActions>
      </Dialog>

      {/* Bulk Create Dialog */}
      <Dialog open={bulkCreateDialog} onClose={() => setBulkCreateDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Bulk Add FAQs</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Enter multiple FAQs using the format below. Separate each FAQ with a blank line.
          </Typography>
          <Alert severity="info" sx={{ mb: 2 }}>
            Format:<br />
            Q: Your question here<br />
            A: Your answer here<br />
            <br />
            Q: Another question<br />
            A: Another answer
          </Alert>
          <TextField
            fullWidth
            multiline
            rows={12}
            placeholder="Q: What is this survey about?\nA: This survey is about...\n\nQ: How long will it take?\nA: Approximately 5 minutes."
            value={bulkFaqs}
            onChange={(e) => setBulkFaqs(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setBulkCreateDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleBulkCreate}>Add FAQs</Button>
        </DialogActions>
      </Dialog>

      {/* Delete Dialog */}
      <Dialog open={deleteDialog.open} onClose={() => setDeleteDialog({ open: false, faq: null })}>
        <DialogTitle>Delete FAQ</DialogTitle>
        <DialogContent>
          <Typography>Are you sure you want to delete this FAQ?</Typography>
          {deleteDialog.faq?.is_trained && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              This FAQ is trained. Deleting it will also remove it from the knowledge base.
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog({ open: false, faq: null })}>Cancel</Button>
          <Button variant="contained" color="error" onClick={handleDeleteFAQ}>Delete</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
      >
        <Alert onClose={() => setSnackbar({ ...snackbar, open: false })} severity={snackbar.severity}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default SurveyFAQs;